import { diffArrays, diffLines } from 'diff';

const DEFAULT_TIMEOUT_MS = 160;
const MAX_TIMEOUT_MS = 500;
const DEFAULT_MAX_EDIT_LENGTH = 4000;
const MAX_EDIT_LENGTH = 12000;
const DEFAULT_MAX_REFINEMENT_CHARS = 50000;
const MAX_REFINEMENT_CHARS = 100000;

export const DOCUMENT_VERSION_DIFF_LIMITS = Object.freeze({
  defaultTimeoutMs: DEFAULT_TIMEOUT_MS,
  maxTimeoutMs: MAX_TIMEOUT_MS,
  defaultMaxEditLength: DEFAULT_MAX_EDIT_LENGTH,
  maxEditLength: MAX_EDIT_LENGTH,
  defaultMaxRefinementChars: DEFAULT_MAX_REFINEMENT_CHARS,
  maxRefinementChars: MAX_REFINEMENT_CHARS,
});

let spanishSegmenter;

/**
 * Preserve the canonical Markdown byte-for-byte except for the two canonical
 * equivalences the editor supports: CRLF line endings and Unicode NFC.
 */
export function normalizeCanonicalMarkdown(value) {
  const text = typeof value === 'string' ? value : String(value ?? '');
  return text.replace(/\r\n/g, '\n').normalize('NFC');
}

function boundedInteger(value, fallback, maximum) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return Math.min(Math.floor(parsed), maximum);
}

function resolveLimits(options) {
  const settings = options && typeof options === 'object' ? options : {};
  return {
    timeout: boundedInteger(
      settings.timeoutMs ?? settings.timeout,
      DEFAULT_TIMEOUT_MS,
      MAX_TIMEOUT_MS,
    ),
    maxEditLength: boundedInteger(
      settings.maxEditLength,
      DEFAULT_MAX_EDIT_LENGTH,
      MAX_EDIT_LENGTH,
    ),
    maxRefinementChars: boundedInteger(
      settings.maxRefinementChars,
      DEFAULT_MAX_REFINEMENT_CHARS,
      MAX_REFINEMENT_CHARS,
    ),
  };
}

function getSpanishSegmenter() {
  if (spanishSegmenter !== undefined) return spanishSegmenter;
  try {
    spanishSegmenter = typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'
      ? new Intl.Segmenter('es', { granularity: 'word' })
      : null;
  } catch (error) {
    spanishSegmenter = null;
  }
  return spanishSegmenter;
}

/**
 * Intl.Segmenter keeps Spanish words (including accents) intact while also
 * returning whitespace and punctuation. The Unicode fallback is deliberately
 * lossless: joining the tokens always recreates the input exactly.
 */
function tokenizeForRefinement(value) {
  if (!value) return [];
  const segmenter = getSpanishSegmenter();
  if (segmenter) {
    return Array.from(segmenter.segment(value), entry => entry.segment);
  }
  return value.match(/\s+|[\p{L}\p{M}\p{N}_]+|[^\s\p{L}\p{M}\p{N}_]/gu) || [];
}

function appendSegment(segments, type, value) {
  if (!value) return;
  const previous = segments[segments.length - 1];
  if (previous && previous.type === type) {
    previous.value += value;
    return;
  }
  segments.push({ type, value });
}

function appendEqualSection(sections, value, blockMetadata = null) {
  if (!value) return;
  const previous = sections[sections.length - 1];
  const previousTracksBlocks = previous && Number.isInteger(previous.blockCount);
  const nextTracksBlocks = blockMetadata && Number.isInteger(blockMetadata.blockCount);
  const blockRangesAreContiguous = previousTracksBlocks && nextTracksBlocks
    && previous.beforeBlockStart + previous.blockCount === blockMetadata.beforeBlockStart
    && previous.afterBlockStart + previous.blockCount === blockMetadata.afterBlockStart;
  if (
    previous
    && previous.type === 'equal'
    && ((!previousTracksBlocks && !nextTracksBlocks) || blockRangesAreContiguous)
  ) {
    previous.value += value;
    if (blockRangesAreContiguous) {
      previous.blockCount += blockMetadata.blockCount;
    }
    return;
  }
  sections.push({
    type: 'equal',
    value,
    ...(blockMetadata || {}),
  });
}

const SPANISH_ORDINAL_WORDS = new Set([
  'primer', 'primero', 'primera',
  'segundo', 'segunda',
  'tercer', 'tercero', 'tercera',
  'cuarto', 'cuarta',
  'quinto', 'quinta',
  'sexto', 'sexta',
  'septimo', 'septima',
  'octavo', 'octava',
  'noveno', 'novena',
  'decimo', 'decima',
  'undecimo', 'undecima',
  'duodecimo', 'duodecima',
  'vigesimo', 'vigesima',
  'trigesimo', 'trigesima',
  'cuadragesimo', 'cuadragesima',
  'quincuagesimo', 'quincuagesima',
  'sexagesimo', 'sexagesima',
  'septuagesimo', 'septuagesima',
  'octogesimo', 'octogesima',
  'nonagesimo', 'nonagesima',
  'centesimo', 'centesima',
]);

function isSpanishOrdinalPhrase(value) {
  const words = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('es')
    .trim()
    .split(/[ \t]+/);
  return words.length > 0 && words.every(word => SPANISH_ORDINAL_WORDS.has(word));
}

function leadingClauseOrdinalRange(block) {
  // Contract clauses are emitted as top-level bold headings, for example
  // `**SÉPTIMA. Gastos**.`. Requiring the heading punctuation and validating
  // every word avoids treating ordinary bold prose as a clause ordinal.
  const boldHeading = /^[ \t]{0,3}\*\*([^\.\r\n]+)\.(?=[ \t])/.exec(block);
  if (boldHeading) {
    const ordinal = boldHeading[1];
    if (/^\d+$/.test(ordinal.trim()) || isSpanishOrdinalPhrase(ordinal)) {
      const start = boldHeading[0].indexOf(ordinal);
      return { start, end: start + ordinal.length };
    }
  }

  // Also accept the plain top-level Markdown ordered-clause form. Limiting
  // indentation to Markdown's top-level allowance excludes nested clauses.
  const numericHeading = /^([ \t]{0,3})(\d+)([.)])(?=[ \t])/.exec(block);
  if (!numericHeading) return null;
  return {
    start: numericHeading[1].length,
    end: numericHeading[1].length + numericHeading[2].length,
  };
}

/**
 * Give a uniquely repeated clause body a shared private comparison identity
 * when only its leading ordinal changed. Raw blocks remain separate and are
 * used to materialize the result, so the ordinal is still a normal edit.
 */
function alignNumberedClauseBlocks(beforeBlocks, afterBlocks) {
  const describe = blocks => blocks.map((block) => {
    const range = leadingClauseOrdinalRange(block);
    if (!range) return null;
    return {
      signature: `${block.slice(0, range.start)}\u0000${block.slice(range.end)}`,
    };
  });
  const beforeDescriptions = describe(beforeBlocks);
  const afterDescriptions = describe(afterBlocks);
  const countSignatures = (descriptions) => {
    const counts = new Map();
    descriptions.forEach((description) => {
      if (!description) return;
      counts.set(description.signature, (counts.get(description.signature) || 0) + 1);
    });
    return counts;
  };
  const beforeCounts = countSignatures(beforeDescriptions);
  const afterCounts = countSignatures(afterDescriptions);
  const identities = new Map();
  beforeDescriptions.forEach((description) => {
    if (
      !description
      || beforeCounts.get(description.signature) !== 1
      || afterCounts.get(description.signature) !== 1
    ) return;
    identities.set(description.signature, {});
  });
  const comparisonUnits = (blocks, descriptions) => blocks.map((block, index) => {
    const description = descriptions[index];
    return (description && identities.get(description.signature)) || block;
  });
  return {
    beforeUnits: comparisonUnits(beforeBlocks, beforeDescriptions),
    afterUnits: comparisonUnits(afterBlocks, afterDescriptions),
  };
}

function lineChangesToSectionPlan(changes) {
  const regions = [];
  const sections = [];
  let before = '';
  let after = '';

  const flush = () => {
    if (!before && !after) return;
    const regionIndex = regions.length;
    regions.push({ before, after });
    sections.push({ type: 'change', regionIndex });
    before = '';
    after = '';
  };

  changes.forEach((change) => {
    if (!change.added && !change.removed) {
      flush();
      appendEqualSection(sections, change.value);
    } else if (change.removed) {
      before += change.value;
    } else if (change.added) {
      after += change.value;
    }
  });
  flush();
  return { regions, sections };
}

function blockChangesToSectionPlan(changes, beforeBlocks, afterBlocks) {
  const regions = [];
  const sections = [];
  let before = '';
  let after = '';
  let beforeBlockOffset = 0;
  let afterBlockOffset = 0;

  const flush = () => {
    if (!before && !after) return;
    const regionIndex = regions.length;
    regions.push({ before, after });
    sections.push({ type: 'change', regionIndex });
    before = '';
    after = '';
  };

  changes.forEach((change) => {
    const blocks = Array.isArray(change.value) ? change.value : [];
    if (!change.added && !change.removed) {
      flush();
      blocks.forEach((_block, index) => {
        const beforeValue = beforeBlocks[beforeBlockOffset + index];
        const afterValue = afterBlocks[afterBlockOffset + index];
        if (beforeValue === afterValue) {
          appendEqualSection(sections, beforeValue, {
            beforeBlockStart: beforeBlockOffset + index,
            afterBlockStart: afterBlockOffset + index,
            blockCount: 1,
          });
          return;
        }
        const regionIndex = regions.length;
        regions.push({ before: beforeValue, after: afterValue });
        sections.push({ type: 'change', regionIndex });
      });
      beforeBlockOffset += blocks.length;
      afterBlockOffset += blocks.length;
    } else if (change.removed) {
      before += beforeBlocks.slice(
        beforeBlockOffset,
        beforeBlockOffset + blocks.length,
      ).join('');
      beforeBlockOffset += blocks.length;
    } else if (change.added) {
      after += afterBlocks.slice(
        afterBlockOffset,
        afterBlockOffset + blocks.length,
      ).join('');
      afterBlockOffset += blocks.length;
    }
  });
  flush();
  return { regions, sections };
}

function splitLinesPreservingEndings(value) {
  return value.match(/[^\n]*\n|[^\n]+$/g) || [];
}

function uniqueUnitPositions(units) {
  const positions = new Map();
  units.forEach((unit, index) => {
    const previous = positions.get(unit);
    positions.set(unit, previous === undefined ? index : null);
  });
  return positions;
}

function repeatedUnitAnchorsInRange(
  beforeUnits,
  afterUnits,
  beforeStart,
  beforeEnd,
  afterStart,
  afterEnd,
) {
  const afterPositions = new Map();
  for (let index = afterStart; index < afterEnd; index += 1) {
    const positions = afterPositions.get(afterUnits[index]) || [];
    positions.push(index);
    afterPositions.set(afterUnits[index], positions);
  }

  const anchors = [];
  let lastAfterIndex = afterStart - 1;
  for (let beforeIndex = beforeStart; beforeIndex < beforeEnd; beforeIndex += 1) {
    const positions = afterPositions.get(beforeUnits[beforeIndex]);
    if (!positions) continue;
    let low = 0;
    let high = positions.length;
    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (positions[middle] <= lastAfterIndex) low = middle + 1;
      else high = middle;
    }
    const afterIndex = positions[low];
    if (!Number.isInteger(afterIndex) || afterIndex >= afterEnd) continue;
    anchors.push({ beforeIndex, afterIndex });
    lastAfterIndex = afterIndex;
  }
  return anchors;
}

/**
 * Linear fallback used when bounded Myers declines a comparison. Unique equal
 * units become ordered anchors, so distant edits do not paint the untouched
 * middle of a long document as one giant replacement.
 */
function buildAnchoredFallbackSectionPlan(beforeUnits, afterUnits, { trackBlocks = false } = {}) {
  let commonPrefixLength = 0;
  while (
    commonPrefixLength < beforeUnits.length
    && commonPrefixLength < afterUnits.length
    && beforeUnits[commonPrefixLength] === afterUnits[commonPrefixLength]
  ) commonPrefixLength += 1;

  let commonSuffixLength = 0;
  while (
    commonSuffixLength < beforeUnits.length - commonPrefixLength
    && commonSuffixLength < afterUnits.length - commonPrefixLength
    && beforeUnits[beforeUnits.length - commonSuffixLength - 1]
      === afterUnits[afterUnits.length - commonSuffixLength - 1]
  ) commonSuffixLength += 1;

  const beforeMiddle = beforeUnits.slice(
    commonPrefixLength,
    beforeUnits.length - commonSuffixLength,
  );
  const afterMiddle = afterUnits.slice(
    commonPrefixLength,
    afterUnits.length - commonSuffixLength,
  );
  const beforePositions = uniqueUnitPositions(beforeMiddle);
  const afterPositions = uniqueUnitPositions(afterMiddle);
  const stableAnchors = [];
  let lastAfterIndex = commonPrefixLength - 1;
  beforeMiddle.forEach((unit, middleBeforeIndex) => {
    if (beforePositions.get(unit) !== middleBeforeIndex) return;
    const middleAfterIndex = afterPositions.get(unit);
    const afterIndex = Number.isInteger(middleAfterIndex)
      ? middleAfterIndex + commonPrefixLength
      : null;
    if (!Number.isInteger(afterIndex) || afterIndex <= lastAfterIndex) return;
    stableAnchors.push({
      beforeIndex: middleBeforeIndex + commonPrefixLength,
      afterIndex,
    });
    lastAfterIndex = afterIndex;
  });

  // Unique anchors protect the overall order. Within each protected gap,
  // greedily retain repeated equal blocks too (common in legal boilerplate).
  const anchors = [];
  let beforeAnchorCursor = commonPrefixLength;
  let afterAnchorCursor = commonPrefixLength;
  stableAnchors.forEach((anchor) => {
    anchors.push(...repeatedUnitAnchorsInRange(
      beforeUnits,
      afterUnits,
      beforeAnchorCursor,
      anchor.beforeIndex,
      afterAnchorCursor,
      anchor.afterIndex,
    ));
    anchors.push(anchor);
    beforeAnchorCursor = anchor.beforeIndex + 1;
    afterAnchorCursor = anchor.afterIndex + 1;
  });
  anchors.push(...repeatedUnitAnchorsInRange(
    beforeUnits,
    afterUnits,
    beforeAnchorCursor,
    beforeUnits.length - commonSuffixLength,
    afterAnchorCursor,
    afterUnits.length - commonSuffixLength,
  ));

  const regions = [];
  const sections = [];
  let beforeCursor = 0;
  let afterCursor = 0;
  const appendGap = (beforeEnd, afterEnd) => {
    const before = beforeUnits.slice(beforeCursor, beforeEnd).join('');
    const after = afterUnits.slice(afterCursor, afterEnd).join('');
    if (!before && !after) return;
    const regionIndex = regions.length;
    regions.push({ before, after });
    sections.push({ type: 'change', regionIndex });
  };

  if (commonPrefixLength) {
    appendEqualSection(
      sections,
      beforeUnits.slice(0, commonPrefixLength).join(''),
      trackBlocks ? {
        beforeBlockStart: 0,
        afterBlockStart: 0,
        blockCount: commonPrefixLength,
      } : null,
    );
    beforeCursor = commonPrefixLength;
    afterCursor = commonPrefixLength;
  }

  anchors.forEach(({ beforeIndex, afterIndex }) => {
    appendGap(beforeIndex, afterIndex);
    appendEqualSection(sections, beforeUnits[beforeIndex], trackBlocks ? {
      beforeBlockStart: beforeIndex,
      afterBlockStart: afterIndex,
      blockCount: 1,
    } : null);
    beforeCursor = beforeIndex + 1;
    afterCursor = afterIndex + 1;
  });
  const beforeSuffixStart = beforeUnits.length - commonSuffixLength;
  const afterSuffixStart = afterUnits.length - commonSuffixLength;
  appendGap(beforeSuffixStart, afterSuffixStart);
  if (commonSuffixLength) {
    appendEqualSection(
      sections,
      beforeUnits.slice(beforeSuffixStart).join(''),
      trackBlocks ? {
        beforeBlockStart: beforeSuffixStart,
        afterBlockStart: afterSuffixStart,
        blockCount: commonSuffixLength,
      } : null,
    );
  }
  return { regions, sections };
}

function buildLineFallbackSectionPlan(before, after) {
  return buildAnchoredFallbackSectionPlan(
    splitLinesPreservingEndings(before),
    splitLinesPreservingEndings(after),
  );
}

function resolveMarkdownBlocks(before, after, options) {
  const settings = options && typeof options === 'object' ? options : {};
  const normalizeBlocks = (value) => (
    Array.isArray(value)
      ? value.map(block => normalizeCanonicalMarkdown(block))
      : null
  );
  const beforeBlocks = normalizeBlocks(settings.previousBlocks);
  const afterBlocks = normalizeBlocks(settings.nextBlocks);
  if (
    !beforeBlocks
    || !afterBlocks
    || beforeBlocks.join('') !== before
    || afterBlocks.join('') !== after
  ) return null;
  return { beforeBlocks, afterBlocks };
}

function asLineLevelHunk(region, id) {
  const before = [];
  const after = [];
  appendSegment(before, 'removed', region.before);
  appendSegment(after, 'added', region.after);
  return {
    id,
    before,
    after,
    hasAddition: Boolean(region.after),
    hasRemoval: Boolean(region.before),
  };
}

function refinedChangesToHunk(changes, id) {
  const before = [];
  const after = [];
  changes.forEach((change) => {
    const value = change.value.join('');
    if (change.removed) {
      appendSegment(before, 'removed', value);
    } else if (change.added) {
      appendSegment(after, 'added', value);
    } else {
      appendSegment(before, 'equal', value);
      appendSegment(after, 'equal', value);
    }
  });

  return {
    id,
    before,
    after,
    hasAddition: after.some(segment => segment.type === 'added'),
    hasRemoval: before.some(segment => segment.type === 'removed'),
  };
}

function refineRegion(region, id, deadline, maxEditLength, maxRefinementChars) {
  if (!region.before || !region.after) {
    return { hunk: asLineLevelHunk(region, id), usedFallback: false };
  }
  if (
    maxEditLength <= 0
    || maxRefinementChars <= 0
    || region.before.length + region.after.length > maxRefinementChars
    || Date.now() >= deadline
  ) {
    return { hunk: asLineLevelHunk(region, id), usedFallback: true };
  }

  const beforeTokens = tokenizeForRefinement(region.before);
  if (Date.now() >= deadline) {
    return { hunk: asLineLevelHunk(region, id), usedFallback: true };
  }
  const afterTokens = tokenizeForRefinement(region.after);
  const remaining = deadline - Date.now();
  if (remaining <= 0) {
    return { hunk: asLineLevelHunk(region, id), usedFallback: true };
  }
  const changes = diffArrays(beforeTokens, afterTokens, {
    timeout: remaining,
    maxEditLength,
  });
  if (!changes) {
    return { hunk: asLineLevelHunk(region, id), usedFallback: true };
  }

  return {
    hunk: refinedChangesToHunk(changes, id),
    usedFallback: false,
  };
}

function runDiffAsync(diffFunction, previous, next, options) {
  return new Promise((resolve, reject) => {
    try {
      diffFunction(previous, next, { ...options, callback: resolve });
    } catch (error) {
      reject(error);
    }
  });
}

async function refineRegionAsync(region, id, deadline, maxEditLength, maxRefinementChars) {
  if (!region.before || !region.after) {
    return { hunk: asLineLevelHunk(region, id), usedFallback: false };
  }
  if (
    maxEditLength <= 0
    || maxRefinementChars <= 0
    || region.before.length + region.after.length > maxRefinementChars
    || Date.now() >= deadline
  ) {
    return { hunk: asLineLevelHunk(region, id), usedFallback: true };
  }

  const beforeTokens = tokenizeForRefinement(region.before);
  if (Date.now() >= deadline) {
    return { hunk: asLineLevelHunk(region, id), usedFallback: true };
  }
  const afterTokens = tokenizeForRefinement(region.after);
  const remaining = deadline - Date.now();
  if (remaining <= 0) {
    return { hunk: asLineLevelHunk(region, id), usedFallback: true };
  }
  const changes = await runDiffAsync(diffArrays, beforeTokens, afterTokens, {
    timeout: remaining,
    maxEditLength,
  });
  if (!changes) {
    return { hunk: asLineLevelHunk(region, id), usedFallback: true };
  }
  return {
    hunk: refinedChangesToHunk(changes, id),
    usedFallback: false,
  };
}

function countDisplayTokens(value) {
  const matcher = /[\p{L}\p{M}\p{N}_]+|[^\s\p{L}\p{M}\p{N}_]/gu;
  let count = 0;
  while (matcher.exec(value)) count += 1;
  return count;
}

function changedTokenCount(hunks, side, type) {
  let hasChangedText = false;
  let nonWhitespaceTokens = 0;
  hunks.forEach((hunk) => {
    hunk[side].forEach((segment) => {
      if (segment.type !== type || !segment.value) return;
      hasChangedText = true;
      nonWhitespaceTokens += countDisplayTokens(segment.value);
    });
  });
  return hasChangedText ? Math.max(1, nonWhitespaceTokens) : 0;
}

function buildCounts(hunks) {
  const additions = changedTokenCount(hunks, 'after', 'added');
  const removals = changedTokenCount(hunks, 'before', 'removed');
  return {
    changes: additions + removals,
    additions,
    removals,
  };
}

function materializeSections(sectionPlan) {
  return sectionPlan.map((section) => {
    if (section.type === 'equal') {
      return {
        type: 'equal',
        value: section.value,
        ...(Number.isInteger(section.blockCount) ? {
          beforeBlockStart: section.beforeBlockStart,
          afterBlockStart: section.afterBlockStart,
          blockCount: section.blockCount,
        } : {}),
      };
    }
    return { type: 'change', hunkIndex: section.regionIndex };
  });
}

function emptyResult(documentValue = '', blockCount = null) {
  const counts = { changes: 0, additions: 0, removals: 0 };
  return {
    hunks: [],
    sections: documentValue ? [{
      type: 'equal',
      value: documentValue,
      ...(Number.isInteger(blockCount) ? {
        beforeBlockStart: 0,
        afterBlockStart: 0,
        blockCount,
      } : {}),
    }] : [],
    counts,
    stats: { ...counts },
    truncated: false,
    fallback: null,
  };
}

/**
 * Compare two canonical Markdown document versions without producing HTML.
 *
 * When the caller supplies exact top-level Markdown blocks, the first pass
 * keeps those structural boundaries intact; otherwise it compares by line.
 * Every equal run remains in an ordered section stream. Replacement regions
 * are then refined with strict Spanish word, punctuation, and whitespace
 * tokens. Every expensive diff call is bounded. If a bound is reached, ordered
 * equal anchors still keep distant untouched context outside the change hunks.
 */
export function buildDocumentVersionDiff(previousMarkdown, nextMarkdown, options = {}) {
  const before = normalizeCanonicalMarkdown(previousMarkdown);
  const after = normalizeCanonicalMarkdown(nextMarkdown);
  const markdownBlocks = resolveMarkdownBlocks(before, after, options);
  const blockAlignment = markdownBlocks
    ? alignNumberedClauseBlocks(markdownBlocks.beforeBlocks, markdownBlocks.afterBlocks)
    : null;
  if (before === after) {
    return emptyResult(before, markdownBlocks ? markdownBlocks.beforeBlocks.length : null);
  }

  const limits = resolveLimits(options);
  const deadline = Date.now() + limits.timeout;
  let primaryChanges;
  if (limits.timeout > 0 && limits.maxEditLength > 0) {
    primaryChanges = markdownBlocks
      ? diffArrays(blockAlignment.beforeUnits, blockAlignment.afterUnits, {
        timeout: limits.timeout,
        maxEditLength: limits.maxEditLength,
      })
      : diffLines(before, after, {
        ignoreWhitespace: false,
        ignoreNewlineAtEof: false,
        newlineIsToken: false,
        timeout: limits.timeout,
        maxEditLength: limits.maxEditLength,
      });
  }

  let fallback = null;
  const sectionPlan = primaryChanges
    ? (
      markdownBlocks
        ? blockChangesToSectionPlan(
          primaryChanges,
          markdownBlocks.beforeBlocks,
          markdownBlocks.afterBlocks,
        )
        : lineChangesToSectionPlan(primaryChanges)
    )
    : (
      markdownBlocks
        ? buildAnchoredFallbackSectionPlan(
          markdownBlocks.beforeBlocks,
          markdownBlocks.afterBlocks,
          { trackBlocks: true },
        )
        : buildLineFallbackSectionPlan(before, after)
    );
  const { regions } = sectionPlan;
  if (!primaryChanges) fallback = 'line';

  const hunks = regions.map((region, index) => {
    const id = `change-${index + 1}`;
    if (!primaryChanges) return asLineLevelHunk(region, id);
    const refined = refineRegion(
      region,
      id,
      deadline,
      limits.maxEditLength,
      limits.maxRefinementChars,
    );
    if (refined.usedFallback) fallback = 'line';
    return refined.hunk;
  });
  const counts = buildCounts(hunks);

  return {
    hunks,
    sections: materializeSections(sectionPlan.sections),
    counts,
    stats: { ...counts },
    truncated: fallback !== null,
    fallback,
  };
}

/**
 * Browser-friendly variant. JsDiff yields between Myers iterations so a large
 * comparison can keep the loading UI, keyboard input, and scrolling responsive.
 * The returned model is identical to the synchronous API used by unit tests.
 */
export async function buildDocumentVersionDiffAsync(previousMarkdown, nextMarkdown, options = {}) {
  const before = normalizeCanonicalMarkdown(previousMarkdown);
  const after = normalizeCanonicalMarkdown(nextMarkdown);
  const markdownBlocks = resolveMarkdownBlocks(before, after, options);
  const blockAlignment = markdownBlocks
    ? alignNumberedClauseBlocks(markdownBlocks.beforeBlocks, markdownBlocks.afterBlocks)
    : null;
  if (before === after) {
    return emptyResult(before, markdownBlocks ? markdownBlocks.beforeBlocks.length : null);
  }

  const limits = resolveLimits(options);
  let primaryChanges;
  if (limits.timeout > 0 && limits.maxEditLength > 0) {
    primaryChanges = markdownBlocks
      ? await runDiffAsync(
        diffArrays,
        blockAlignment.beforeUnits,
        blockAlignment.afterUnits,
        {
          timeout: limits.timeout,
          maxEditLength: limits.maxEditLength,
        },
      )
      : await runDiffAsync(diffLines, before, after, {
        ignoreWhitespace: false,
        ignoreNewlineAtEof: false,
        newlineIsToken: false,
        timeout: limits.timeout,
        maxEditLength: limits.maxEditLength,
      });
  }

  let fallback = null;
  const sectionPlan = primaryChanges
    ? (
      markdownBlocks
        ? blockChangesToSectionPlan(
          primaryChanges,
          markdownBlocks.beforeBlocks,
          markdownBlocks.afterBlocks,
        )
        : lineChangesToSectionPlan(primaryChanges)
    )
    : (
      markdownBlocks
        ? buildAnchoredFallbackSectionPlan(
          markdownBlocks.beforeBlocks,
          markdownBlocks.afterBlocks,
          { trackBlocks: true },
        )
        : buildLineFallbackSectionPlan(before, after)
    );
  const { regions } = sectionPlan;
  if (!primaryChanges) fallback = 'line';
  // Async Myers iterations intentionally yield to the browser event loop, so
  // refinement receives its own wall-clock budget after the structural pass.
  const deadline = Date.now() + limits.timeout;

  const hunks = [];
  for (let index = 0; index < regions.length; index += 1) {
    const region = regions[index];
    const id = `change-${index + 1}`;
    if (!primaryChanges) {
      hunks.push(asLineLevelHunk(region, id));
      continue;
    }
    const refined = await refineRegionAsync(
      region,
      id,
      deadline,
      limits.maxEditLength,
      limits.maxRefinementChars,
    );
    if (refined.usedFallback) fallback = 'line';
    hunks.push(refined.hunk);
  }
  const counts = buildCounts(hunks);

  return {
    hunks,
    sections: materializeSections(sectionPlan.sections),
    counts,
    stats: { ...counts },
    truncated: fallback !== null,
    fallback,
  };
}

export const compareDocumentVersions = buildDocumentVersionDiff;
